import { ChangeDetectorRef, Inject, OnDestroy, Optional, Pipe, PipeTransform } from '@angular/core';
import { CultureSelectors, CultureSetted } from '@foxeet/chests/culture';
import { InternationalizationTypes } from '@foxeet/domain';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { isNilOrEmptyString } from '@foxeet/utils/functions';

// Importación dinámica del AuthService para evitar dependencia circular
type AuthServiceType = { isOverseerManager: () => boolean };

@Pipe({ name: 'translate', pure: false })
export class InternationalizationPipe extends TranslatePipe implements PipeTransform, OnDestroy {
  private subs = new Subscription();
  private _cultureSetted: CultureSetted | null = null;
  private _authService: AuthServiceType | null = null;

  constructor(
    protected _translateService: TranslateService, 
    protected _changeDetectorRef: ChangeDetectorRef, 
    private store: Store,
    @Optional() @Inject('AuthService') authService: AuthServiceType | null
  ) {
    super(_translateService, _changeDetectorRef);
    this._authService = authService;
    this.subs.add(
      this.store.select(CultureSelectors.CultureSetted).subscribe((values) => {
        this._cultureSetted = values;
      }),
    );
  }

  public override transform(value: string, type?: InternationalizationTypes, args = {}): string {
    let customArg = {};
    switch (type) {
      case InternationalizationTypes.Currency:
        customArg = { currency: isNilOrEmptyString(this._cultureSetted?.reportCurrency) ? this._cultureSetted?.currency : this._cultureSetted?.reportCurrency };
        break;
      case InternationalizationTypes.UnitLength:
        customArg = { unitLength: this._cultureSetted?.unitLength };
        break;
      case InternationalizationTypes.SurfaceUnit:
        customArg = { surfaceUnit: this._cultureSetted?.surfaceUnit };
        break;
      case InternationalizationTypes.CurrencyPerSurfaceUnit:
        customArg = {
          currency_per_surfaceUnit: isNilOrEmptyString(this._cultureSetted?.reportCurrencyPerSurfaceUnit)
            ? this._cultureSetted?.currencyPerSurfaceUnit
            : this._cultureSetted?.reportCurrencyPerSurfaceUnit,
        };
        break;
    }
    
    let translatedValue = super.transform(value, { ...args, ...customArg });

    // Para el rol OverseerManager, reemplazar referencias a prescriptor por "Otro contacto"
    if (this._authService?.isOverseerManager?.()) {
      translatedValue = this._replacePrescriptorTerms(translatedValue);
    }

    return translatedValue;
  }

  private _replacePrescriptorTerms(text: string): string {
    if (!text) return text;
    
    // Español
    text = text.replace(/Prescriptores/gi, (match) => match[0] === 'P' ? 'Otros contactos' : 'otros contactos');
    text = text.replace(/Prescriptor/gi, (match) => match[0] === 'P' ? 'Otro contacto' : 'otro contacto');
    
    // Inglés
    text = text.replace(/Prescribers/gi, (match) => match[0] === 'P' ? 'Other contacts' : 'other contacts');
    text = text.replace(/Prescriber/gi, (match) => match[0] === 'P' ? 'Other contact' : 'other contact');
    
    // Portugués (prescritor y prescriptor)
    text = text.replace(/Prescritores/gi, (match) => match[0] === 'P' ? 'Outros contactos' : 'outros contactos');
    text = text.replace(/Prescritor/gi, (match) => match[0] === 'P' ? 'Outro contacto' : 'outro contacto');
    text = text.replace(/Prescriptores/gi, (match) => match[0] === 'P' ? 'Outros contactos' : 'outros contactos');
    text = text.replace(/Prescriptor/gi, (match) => match[0] === 'P' ? 'Outro contacto' : 'outro contacto');
    
    return text;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subs.unsubscribe();
  }
}
